import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Lock, Globe, Clock, Camera, Save, AlertTriangle, Trash2, CheckCircle, ChevronLeft, Shield, Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function ProfilePage() {
  // Personal Info State
  const [firstName, setFirstName] = useState('Linda');
  const [lastName, setLastName] = useState('Taylor');
  const [email] = useState('linda.taylor@example.com'); // Read-only
  const [avatar, setAvatar] = useState<string | null>(null);
  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Preferences State
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC-5');
  // UI State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const handleSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1661">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1662">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1663">
          <div className="flex items-center h-16" data-id="element-1664">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-1665">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1666">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1667">
                <Home size={18} data-id="element-1668" />
                <span className="hidden sm:inline" data-id="element-1669">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1670">
                <BarChart3 size={18} data-id="element-1671" />
                <span className="hidden sm:inline" data-id="element-1672">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1673">
                <FileText size={18} data-id="element-1674" />
                <span className="hidden sm:inline" data-id="element-1675">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1676">
                <UserCog size={18} data-id="element-1677" />
                <span className="hidden sm:inline" data-id="element-1678">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1679">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1680">
                <Bell size={20} data-id="element-1681" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1682"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1683">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1684">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1685">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1686">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1687" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1688">
                    <div className="py-2" data-id="element-1689">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1690">
                        <Users size={18} className="text-gray-600" data-id="element-1691" />
                        <span className="font-medium text-gray-900" data-id="element-1692">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1693">
                        <Home size={18} className="text-gray-600" data-id="element-1694" />
                        <span className="font-medium text-gray-900" data-id="element-1695">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1696">
                        <UserCog size={18} className="text-gray-600" data-id="element-1697" />
                        <span className="font-medium text-gray-900" data-id="element-1698">
                          Admin Hub
                        </span>
                      </Link>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1699">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1700">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-1701"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-1702"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1703">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-1704"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1705">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1706">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1707"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1708"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1709"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1710">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1711">
        <div className="mb-8" data-id="element-1712">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1713">Profile</h1>
          <p className="text-gray-600" data-id="element-1714">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1715">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3" data-id="element-1716">
            <nav className="space-y-1 sticky top-24" data-id="element-1717">
              <button onClick={() => setActiveTab('general')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1718">
                <User size={18} data-id="element-1719" />
                General
              </button>
              <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'security' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1720">
                <Shield size={18} data-id="element-1721" />
                Security
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'preferences' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-1722">
                <Globe size={18} data-id="element-1723" />
                Preferences
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6" data-id="element-1724">
            {/* General Section */}
            {activeTab === 'general' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-1725">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1726">
                  <div className="p-6 border-b border-gray-200" data-id="element-1727">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-1728">
                      Personal Information
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1729">
                      Update your photo and personal details.
                    </p>
                  </div>

                  <div className="p-6 space-y-8" data-id="element-1730">
                    {/* Avatar Upload */}
                    <div className="flex items-center gap-6" data-id="element-1731">
                      <div className="relative group" data-id="element-1732">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md" data-id="element-1733">
                          {avatar ? <img src={avatar} alt="Profile" className="w-full h-full object-cover" data-id="element-1734" /> : <span className="text-2xl font-bold text-gray-500" data-id="element-1735">
                              LT
                            </span>}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-id="element-1736">
                            <Camera className="text-white" size={24} data-id="element-1737" />
                          </div>
                        </div>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-1738" />
                      </div>
                      <div data-id="element-1739">
                        <h3 className="font-medium text-gray-900" data-id="element-1740">
                          Profile Photo
                        </h3>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-1741">
                          This will be displayed on your profile.
                        </p>
                        <div className="flex gap-3" data-id="element-1742">
                          <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] relative" data-id="element-1743">
                            Change
                            <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-1744" />
                          </button>
                          <button onClick={() => setAvatar(null)} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-1745">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1746">
                      <div data-id="element-1747">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1748">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-1749" />
                      </div>
                      <div data-id="element-1750">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1751">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-1752" />
                      </div>
                    </div>

                    <div data-id="element-1753">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1754">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-1755">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1756" />
                        <input type="email" value={email} readOnly className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed" data-id="element-1757" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1758">
                        Contact support to change your email address.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-1759">
                    <button onClick={handleSave} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-1760">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-1761" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-1762" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Security Section */}
            {activeTab === 'security' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-1763">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1764">
                  <div className="p-6 border-b border-gray-200" data-id="element-1765">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-1766">
                      Password & Security
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1767">
                      Manage your password and account security.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-1768">
                    <div data-id="element-1769">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1770">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-1771">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1772" />
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Enter current password" data-id="element-1773" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1774">
                      <div data-id="element-1775">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1776">
                          New Password
                        </label>
                        <div className="relative" data-id="element-1777">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1778" />
                          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Min. 8 characters" data-id="element-1779" />
                        </div>
                      </div>
                      <div data-id="element-1780">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1781">
                          Confirm New Password
                        </label>
                        <div className="relative" data-id="element-1782">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1783" />
                          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Re-enter new password" data-id="element-1784" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-1785">
                      <h4 className="text-sm font-medium text-blue-900 mb-2" data-id="element-1786">
                        Password Requirements
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside" data-id="element-1787">
                        <li data-id="element-1788">Minimum 8 characters long</li>
                        <li data-id="element-1789">At least one uppercase letter</li>
                        <li data-id="element-1790">At least one number</li>
                        <li data-id="element-1791">At least one special character</li>
                      </ul>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-1792">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-1793">
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-1794">
                  <div className="p-6 border-b border-red-100 bg-red-50" data-id="element-1795">
                    <h2 className="text-lg font-bold text-red-900 flex items-center gap-2" data-id="element-1796">
                      <AlertTriangle size={20} data-id="element-1797" />
                      Danger Zone
                    </h2>
                    <p className="text-sm text-red-700 mt-1" data-id="element-1798">
                      Irreversible actions for your account.
                    </p>
                  </div>

                  <div className="p-6 flex items-center justify-between flex-wrap gap-4" data-id="element-1799">
                    <div data-id="element-1800">
                      <h3 className="font-medium text-gray-900" data-id="element-1801">
                        Delete Account
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-1802">
                        Permanently delete your account and all of your content.
                      </p>
                    </div>
                    <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium" data-id="element-1803">
                      Delete Account
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Preferences Section */}
            {activeTab === 'preferences' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-1804">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1805">
                  <div className="p-6 border-b border-gray-200" data-id="element-1806">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-1807">
                      Preferences
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-1808">
                      Customize your platform experience.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-1809">
                    <div data-id="element-1810">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1811">
                        Language
                      </label>
                      <div className="relative" data-id="element-1812">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1813" />
                        <select value={language} onChange={e => setLanguage(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent appearance-none bg-white" data-id="element-1814">
                          <option value="en" data-id="element-1815">English (US)</option>
                          <option value="es" data-id="element-1816">Español</option>
                          <option value="fr" data-id="element-1817">Français</option>
                          <option value="de" data-id="element-1818">Deutsch</option>
                          <option value="zh" data-id="element-1819">中文 (Chinese)</option>
                          <option value="ja" data-id="element-1820">日本語 (Japanese)</option>
                        </select>
                      </div>
                    </div>

                    <div data-id="element-1821">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1822">
                        Time Zone
                      </label>
                      <div className="relative" data-id="element-1823">
                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1824" />
                        <select value={timezone} onChange={e => setTimezone(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent appearance-none bg-white" data-id="element-1825">
                          <option value="UTC-8" data-id="element-1826">
                            Pacific Time (US & Canada) (UTC-08:00)
                          </option>
                          <option value="UTC-7" data-id="element-1827">
                            Mountain Time (US & Canada) (UTC-07:00)
                          </option>
                          <option value="UTC-6" data-id="element-1828">
                            Central Time (US & Canada) (UTC-06:00)
                          </option>
                          <option value="UTC-5" data-id="element-1829">
                            Eastern Time (US & Canada) (UTC-05:00)
                          </option>
                          <option value="UTC+0" data-id="element-1830">
                            Greenwich Mean Time (UTC+00:00)
                          </option>
                          <option value="UTC+1" data-id="element-1831">
                            Central European Time (UTC+01:00)
                          </option>
                          <option value="UTC+8" data-id="element-1832">
                            China Standard Time (UTC+08:00)
                          </option>
                          <option value="UTC+9" data-id="element-1833">
                            Japan Standard Time (UTC+09:00)
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-1834">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-1835">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence data-id="element-1836">
        {showDeleteModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)} data-id="element-1837">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-1838">
              <div className="flex items-center gap-3 mb-4" data-id="element-1839">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-1840">
                  <Trash2 size={24} className="text-red-600" data-id="element-1841" />
                </div>
                <div data-id="element-1842">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-1843">
                    Delete Account?
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-1844">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6" data-id="element-1845">
                <p className="text-sm text-red-800" data-id="element-1846">
                  Are you sure you want to delete your account? All of your
                  data, including course progress and certificates, will be
                  permanently removed.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-1847">
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-1848">
                  Cancel
                </button>
                <button onClick={() => {
              // Handle delete logic
              setShowDeleteModal(false);
              alert('Account deletion request sent');
            }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm" data-id="element-1849">
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}